import axios from 'axios';
import { useAuthStore } from '@/store/authStore';
import { isOnPublicAuthPage } from '@/utils/helpers/common';

export const getAccessToken = () => useAuthStore.getState().getAccessToken();

export const setAccessToken = (token: string) => {
  useAuthStore.getState().setAccessToken(token);
};

export const clearAccessToken = () => {
  useAuthStore.getState().clearAuth();
};

export const navigateToSignin = () => {
  if (!isOnPublicAuthPage(window.location.pathname)) {
    window.location.href = '/signin';
  }
};

const API_URL = import.meta.env.VITE_API_URL;

export const axiosInstance = axios.create({
  baseURL: API_URL,
  validateStatus(status) {
    return status >= 200 && status <= 300;
  },
  transformResponse: [
    (data) => {
      try {
        return JSON.parse(data).data;
      } catch (error) {
        console.error('Error parsing response:', error);
        return data;
      }
    },
  ],
});

axiosInstance.interceptors.request.use(
  async (config) => {
    // FOR PUBLIC ENDPOINTS, AUTH IS NOT REQUIRED
    if (config.headers.isPublic) {
      delete config.headers.isPublic;
      return config;
    }

    const authStore = useAuthStore.getState();
    let token = authStore.getAccessToken();

    // IF TOKEN DOES NOT EXIST AND IS NOT ON PUBLIC AUTH PAGE, REFRESH TOKEN
    if (!token && !isOnPublicAuthPage(window.location.pathname)) {
      const isAuthenticated = await authStore.refreshToken();

      if (!isAuthenticated) {
        navigateToSignin();
        return config;
      }

      token = authStore.getAccessToken();
    }

    // IF TOKEN EXISTS, ADD TO HEADER
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;

      // IF TOKEN IS VALID, CHECK AUTH
      if (
        !isOnPublicAuthPage(window.location.pathname) &&
        !authStore.isAuthenticated()
      ) {
        navigateToSignin();
      }
    }

    return config;
  },
  (error) => {
    return Promise.reject(error);
  }
);

axiosInstance.interceptors.response.use(
  (response) => response.data,
  async (error) => {
    const { config } = error;
    const originalRequest = config;
    const status = error?.response?.status;

    if (status === 401 && !originalRequest._retry) {
      originalRequest._retry = true;

      const authStore = useAuthStore.getState();
      const isAuthenticated = await authStore.refreshToken();

      if (isAuthenticated) {
        const newToken = authStore.getAccessToken();
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return axiosInstance(originalRequest);
      }

      navigateToSignin();
    }

    return Promise.reject(error);
  }
);
